import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { FamilyProvider } from "@/contexts/FamilyContext";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Account from "@/pages/Account";
import Prescriptions from "@/pages/Prescriptions";
import PrescriptionUpload from "@/pages/PrescriptionUpload";
import PrescriptionDetail from "@/pages/PrescriptionDetail";
import Checkout from "@/pages/Checkout";
import CheckoutReturn from "@/pages/CheckoutReturn";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Chat from "@/pages/Chat";
import Wallet from "@/pages/Wallet";
import Subscriptions from "@/pages/Subscriptions";
import SubscriptionDetail from "@/pages/SubscriptionDetail";
import Notifications from "@/pages/Notifications";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPrescriptions from "@/pages/admin/AdminPrescriptions";
import AdminPrescriptionDetail from "@/pages/admin/AdminPrescriptionDetail";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductEdit from "@/pages/admin/AdminProductEdit";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminReturns from "@/pages/admin/AdminReturns";
import AdminReturnDetail from "@/pages/admin/AdminReturnDetail";
import AdminPromos from "@/pages/admin/AdminPromos";
import AdminEliteSubscriptions from "@/pages/admin/AdminEliteSubscriptions";
import Returns from "@/pages/Returns";
import ReturnNew from "@/pages/ReturnNew";
import ReturnDetail from "@/pages/ReturnDetail";
import Elite from "@/pages/Elite";
import ReferEarn from "@/pages/ReferEarn";
import Conditions from "@/pages/Conditions";
import ConditionDetail from "@/pages/ConditionDetail";
import HealthTimeline from "@/pages/HealthTimeline";
// v2C — Consultations, Labs, Insurance
import Consultations from "@/pages/Consultations";
import DoctorProfile from "@/pages/DoctorProfile";
import MyConsultations from "@/pages/MyConsultations";
import ConsultationDetail from "@/pages/ConsultationDetail";
import LabTests from "@/pages/LabTests";
import LabTestDetail from "@/pages/LabTestDetail";
import LabPackageDetail from "@/pages/LabPackageDetail";
import LabBooking from "@/pages/LabBooking";
import LabOrders from "@/pages/LabOrders";
import LabOrderDetail from "@/pages/LabOrderDetail";
import Insurance from "@/pages/Insurance";
import InsuranceDetail from "@/pages/InsuranceDetail";
import AdminConsultations from "@/pages/admin/AdminConsultations";
import AdminConsultationDetail from "@/pages/admin/AdminConsultationDetail";
import AdminLabOrders from "@/pages/admin/AdminLabOrders";
import AdminLabOrderDetail from "@/pages/admin/AdminLabOrderDetail";
import AdminInsuranceLeads from "@/pages/admin/AdminInsuranceLeads";
import AdminInventoryForecast from "@/pages/admin/AdminInventoryForecast";
// v2E — Support tickets + Voice + Sentiment
import Support from "@/pages/Support";
import SupportNew from "@/pages/SupportNew";
import SupportThread from "@/pages/SupportThread";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminSupportDetail from "@/pages/admin/AdminSupportDetail";
import AdminChatConversations from "@/pages/admin/AdminChatConversations";
// v2F — B2B portal
import BusinessLanding from "@/pages/BusinessLanding";
import BusinessRegister from "@/pages/BusinessRegister";
import BusinessPending from "@/pages/BusinessPending";
import BusinessDashboard from "@/pages/BusinessDashboard";
import BusinessShop from "@/pages/BusinessShop";
import BusinessCart from "@/pages/BusinessCart";
import BusinessOrders from "@/pages/BusinessOrders";
import BusinessInvoices from "@/pages/BusinessInvoices";
// v2F admin
import AdminB2BAccounts from "@/pages/admin/AdminB2BAccounts";
import AdminB2BOrders from "@/pages/admin/AdminB2BOrders";
import AdminB2BOrderDetail from "@/pages/admin/AdminB2BOrderDetail";
import AdminVendors from "@/pages/admin/AdminVendors";
import AdminPurchaseOrders from "@/pages/admin/AdminPurchaseOrders";
import AdminPurchaseOrderDetail from "@/pages/admin/AdminPurchaseOrderDetail";
// v2H — Inventory ERP Deep + Security Polish
import Security from "@/pages/Security";
import AdminWarehouse from "@/pages/admin/AdminWarehouse";
import AdminRecalls from "@/pages/admin/AdminRecalls";
import AdminMovements from "@/pages/admin/AdminMovements";
import AdminBatches from "@/pages/admin/AdminBatches";
import AdminBulkUpload from "@/pages/admin/AdminBulkUpload";
// v3C — Complaints + KB
import Complaints from "@/pages/Complaints";
import ComplaintWizard from "@/pages/ComplaintWizard";
import ComplaintDetail from "@/pages/ComplaintDetail";
import HelpKB from "@/pages/HelpKB";
import AdminComplaints from "@/pages/admin/AdminComplaints";
import AdminComplaintDetail from "@/pages/admin/AdminComplaintDetail";
import AdminKb from "@/pages/admin/AdminKb";
// v3D — UI Text CMS
import AdminCms from "@/pages/admin/AdminCms";
// v3F — Reminder Analytics + Refer & Earn admin
import AdminRemindersAnalytics from "@/pages/admin/AdminRemindersAnalytics";
import AdminReferEarn from "@/pages/admin/AdminReferEarn";
// Phase 2a — Sendbird unified chat
import ChatSendbird from "@/pages/ChatSendbird";
// Phase 2b — Sendbird UIKit provider root (wraps all routes so <GroupChannel> embeds work anywhere)
import SendbirdRoot from "@/components/sendbird/SendbirdRoot";
import { SendbirdSessionProvider } from "@/hooks/useSendbird";
// Static / informational pages (Truemeds-style footer)
import About from "@/pages/static/About";
import Articles from "@/pages/static/Articles";
import Stories from "@/pages/static/Stories";
import Library from "@/pages/static/Library";
import GenericMedicines from "@/pages/static/GenericMedicines";
import AllBrands from "@/pages/static/AllBrands";
import FAQ from "@/pages/static/FAQ";
import SavingsCalculator from "@/pages/static/SavingsCalculator";
import MobileApp from "@/pages/static/MobileApp";
import { Terms, Privacy, Editorial, Returns as LegalReturns, LowestPrice, ContentDisclaimer } from "@/pages/static/legal/LegalPages";
import Reminders from "@/pages/Reminders";
import AddMedicineWizard from "@/pages/AddMedicineWizard";
import MedicationDetail from "@/pages/MedicationDetail";
// v3E.2 — Family / Caregivers / Chronic / Health
import Family from "@/pages/Family";
import Caregivers from "@/pages/Caregivers";
import ChronicPrograms from "@/pages/ChronicPrograms";
import ChronicProgramDetail from "@/pages/ChronicProgramDetail";
import Health from "@/pages/Health";
// v3E.3 — Vaccinations / Appointments / Lab reminders / Emergency / Wearables
import Vaccinations from "@/pages/Vaccinations";
import Appointments from "@/pages/Appointments";
import LabReminders from "@/pages/LabReminders";
import Emergency from "@/pages/Emergency";
import EmergencyPublic from "@/pages/EmergencyPublic";
import Wearables from "@/pages/Wearables";
import { Navigate } from "react-router-dom";

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <ThemeProvider>
                    <AuthProvider>
                        <LanguageProvider>
                        <CartProvider>
                        <FamilyProvider>
                            <SendbirdSessionProvider>
                            <SendbirdRoot>
                            <Toaster position="top-center" richColors closeButton />
                            <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/signup" element={<Signup />} />
                            <Route path="/products" element={<Products />} />
                            <Route path="/products/:id" element={<ProductDetail />} />
                            <Route path="/cart" element={<Cart />} />
                            <Route path="/account" element={<Account />} />
                            <Route path="/account/security" element={<Security />} />
                            {/* Static / informational pages (footer-linked) */}
                            <Route path="/about" element={<About />} />
                            <Route path="/articles" element={<Articles />} />
                            <Route path="/stories" element={<Stories />} />
                            <Route path="/library" element={<Library />} />
                            <Route path="/generic-medicines" element={<GenericMedicines />} />
                            <Route path="/brands" element={<AllBrands />} />
                            <Route path="/faq" element={<FAQ />} />
                            <Route path="/savings-calculator" element={<SavingsCalculator />} />
                            <Route path="/mobile-app" element={<MobileApp />} />
                            <Route path="/reminders" element={<Reminders />} />
                            <Route path="/reminders/add" element={<AddMedicineWizard />} />
                            <Route path="/reminders/:id" element={<MedicationDetail />} />
                            <Route path="/legal/terms" element={<Terms />} />
                            <Route path="/legal/privacy" element={<Privacy />} />
                            <Route path="/legal/editorial" element={<Editorial />} />
                            <Route path="/legal/returns" element={<LegalReturns />} />
                            <Route path="/legal/lowest-price" element={<LowestPrice />} />
                            <Route path="/legal/content-disclaimer" element={<ContentDisclaimer />} />
                            <Route path="/prescriptions" element={<Prescriptions />} />
                            <Route path="/prescriptions/upload" element={<PrescriptionUpload />} />
                            <Route path="/prescriptions/:id" element={<PrescriptionDetail />} />
                            <Route path="/checkout" element={<Checkout />} />
                            <Route path="/checkout/return" element={<CheckoutReturn />} />
                            <Route path="/orders" element={<Orders />} />
                            <Route path="/orders/:id" element={<OrderDetail />} />
                            <Route path="/chat" element={<ChatSendbird />} />
                            <Route path="/chat/ai" element={<Chat />} />
                            <Route path="/chat/ai/:id" element={<Chat />} />
                            <Route path="/wallet" element={<Wallet />} />
                            <Route path="/subscriptions" element={<Subscriptions />} />
                            <Route path="/subscriptions/:id" element={<SubscriptionDetail />} />
                            <Route path="/notifications" element={<Notifications />} />
                            {/* v2A — Returns, Elite, Refer & Earn */}
                            <Route path="/returns" element={<Returns />} />
                            <Route path="/returns/new" element={<ReturnNew />} />
                            <Route path="/returns/:id" element={<ReturnDetail />} />
                            <Route path="/elite" element={<Elite />} />
                            <Route path="/refer-earn" element={<ReferEarn />} />
                            {/* v2B — Conditions + Health Timeline */}
                            <Route path="/conditions" element={<Conditions />} />
                            <Route path="/conditions/:slug" element={<ConditionDetail />} />
                            <Route path="/timeline" element={<HealthTimeline />} />
                            {/* v2C — Doctor consultations */}
                            <Route path="/consultations" element={<Consultations />} />
                            <Route path="/consultations/my" element={<MyConsultations />} />
                            <Route path="/consultations/doctor/:id" element={<DoctorProfile />} />
                            <Route path="/consultations/:id" element={<ConsultationDetail />} />
                            {/* v2C — Lab tests */}
                            <Route path="/lab-tests" element={<LabTests />} />
                            <Route path="/lab-tests/book" element={<LabBooking />} />
                            <Route path="/lab-tests/:slug" element={<LabTestDetail />} />
                            <Route path="/lab-packages/:slug" element={<LabPackageDetail />} />
                            <Route path="/lab-orders" element={<LabOrders />} />
                            <Route path="/lab-orders/:id" element={<LabOrderDetail />} />
                            {/* v2C — Insurance */}
                            <Route path="/insurance" element={<Insurance />} />
                            <Route path="/insurance/:slug" element={<InsuranceDetail />} />
                            {/* Admin OS */}
                            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin/dashboard" element={<AdminDashboard />} />
                            <Route path="/admin/prescriptions" element={<AdminPrescriptions />} />
                            <Route path="/admin/prescriptions/:id" element={<AdminPrescriptionDetail />} />
                            <Route path="/admin/products" element={<AdminProducts />} />
                            <Route path="/admin/products/bulk-upload" element={<AdminBulkUpload />} />
                            <Route path="/admin/products/:id/edit" element={<AdminProductEdit />} />
                            <Route path="/admin/inventory" element={<AdminInventory />} />
                            <Route path="/admin/inventory/warehouse" element={<AdminWarehouse />} />
                            <Route path="/admin/inventory/batches" element={<AdminBatches />} />
                            <Route path="/admin/inventory/recalls" element={<AdminRecalls />} />
                            <Route path="/admin/inventory/movements" element={<AdminMovements />} />
                            <Route path="/admin/orders" element={<AdminOrders />} />
                            <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
                            <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
                            <Route path="/admin/users" element={<AdminUsers />} />
                            <Route path="/admin/users/:id" element={<AdminUserDetail />} />
                            <Route path="/admin/audit" element={<AdminAudit />} />
                            <Route path="/admin/returns" element={<AdminReturns />} />
                            <Route path="/admin/returns/:id" element={<AdminReturnDetail />} />
                            <Route path="/admin/promos" element={<AdminPromos />} />
                            <Route path="/admin/elite/subscriptions" element={<AdminEliteSubscriptions />} />
                            {/* v2C admin */}
                            <Route path="/admin/consultations" element={<AdminConsultations />} />
                            <Route path="/admin/consultations/:id" element={<AdminConsultationDetail />} />
                            <Route path="/admin/lab-orders" element={<AdminLabOrders />} />
                            <Route path="/admin/lab-orders/:id" element={<AdminLabOrderDetail />} />
                            <Route path="/admin/insurance-leads" element={<AdminInsuranceLeads />} />
                            {/* v2D admin — AI Power-ups */}
                            <Route path="/admin/inventory/forecast" element={<AdminInventoryForecast />} />
                            {/* v2E — customer support */}
                            <Route path="/support" element={<Support />} />
                            <Route path="/support/new" element={<SupportNew />} />
                            <Route path="/support/:id" element={<SupportThread />} />
                            {/* v2E admin */}
                            <Route path="/admin/support" element={<AdminSupport />} />
                            <Route path="/admin/support/:id" element={<AdminSupportDetail />} />
                            <Route path="/admin/chat-conversations" element={<AdminChatConversations />} />
                            {/* v2F — B2B portal (buyer) */}
                            <Route path="/business" element={<BusinessLanding />} />
                            <Route path="/business/register" element={<BusinessRegister />} />
                            <Route path="/business/pending" element={<BusinessPending />} />
                            <Route path="/business/dashboard" element={<BusinessDashboard />} />
                            <Route path="/business/shop" element={<BusinessShop />} />
                            <Route path="/business/cart" element={<BusinessCart />} />
                            <Route path="/business/orders" element={<BusinessOrders />} />
                            <Route path="/business/orders/:id" element={<BusinessOrders />} />
                            <Route path="/business/invoices" element={<BusinessInvoices />} />
                            <Route path="/business/account" element={<Navigate to="/account" replace />} />
                            {/* v2F admin — Wholesale OS */}
                            <Route path="/admin/b2b/accounts" element={<AdminB2BAccounts />} />
                            <Route path="/admin/b2b/orders" element={<AdminB2BOrders />} />
                            <Route path="/admin/b2b/orders/:id" element={<AdminB2BOrderDetail />} />
                            <Route path="/admin/vendors" element={<AdminVendors />} />
                            <Route path="/admin/purchase-orders" element={<AdminPurchaseOrders />} />
                            <Route path="/admin/purchase-orders/:id" element={<AdminPurchaseOrderDetail />} />
                            {/* v3C — Complaints + KB */}
                            <Route path="/complaints" element={<Complaints />} />
                            <Route path="/complaints/new" element={<ComplaintWizard />} />
                            <Route path="/complaints/:id" element={<ComplaintDetail />} />
                            <Route path="/help/kb" element={<HelpKB />} />
                            <Route path="/admin/complaints" element={<AdminComplaints />} />
                            <Route path="/admin/complaints/:id" element={<AdminComplaintDetail />} />
                            <Route path="/admin/kb" element={<AdminKb />} />
                            <Route path="/admin/cms" element={<AdminCms />} />
                            {/* v3F — Admin analytics */}
                            <Route path="/admin/reminders-analytics" element={<AdminRemindersAnalytics />} />
                            <Route path="/admin/refer-earn" element={<AdminReferEarn />} />
                            {/* v3E.2 — Family, Caregivers, Chronic, Health */}
                            <Route path="/family" element={<Family />} />
                            <Route path="/caregivers" element={<Caregivers />} />
                            <Route path="/chronic-programs" element={<ChronicPrograms />} />
                            <Route path="/chronic-programs/:id" element={<ChronicProgramDetail />} />
                            <Route path="/health" element={<Health />} />
                            {/* v3E.3 — Vaccinations, Appointments, Lab reminders, Emergency, Wearables */}
                            <Route path="/vaccinations" element={<Vaccinations />} />
                            <Route path="/appointments" element={<Appointments />} />
                            <Route path="/lab-reminders" element={<LabReminders />} />
                            <Route path="/emergency" element={<Emergency />} />
                            <Route path="/wearables" element={<Wearables />} />
                            <Route path="/emergency/public/:token" element={<EmergencyPublic />} />
                            <Route path="/chronic-programs/:slug" element={<ChronicProgramDetail />} />
                            <Route path="/health" element={<Health />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                            </SendbirdRoot>
                            </SendbirdSessionProvider>
                        </FamilyProvider>
                        </CartProvider>
                        </LanguageProvider>
                    </AuthProvider>
                </ThemeProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
